
import type { RoleAssignmentDTO, UserInviteService } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { ErrorMessage, UserRoleManagementMessages } from '../../models'
import { computed, defineComponent, ref } from 'vue'
import { useAppLoading } from '../../composables/use-app-loading'
import { useUsersError } from '../../composables/users/use-users-error'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { UiModal } from '../keen-ui'
import EpbAccordion from '../shared/epb-accordion.component.vue'
import { EpbButton } from '../shared/epb-button'
import { EpbKeywords } from '../shared/epb-keywords'
import RoleAssignments from './role-assignments.component.vue'

type Error = Omit<ErrorMessage, 'message'> & ({ message: string } | { messages: Array<string> })

export default defineComponent({
  name: 'UserInviteModal',
  components: {
    EpbAccordion,
    EpbButton,
    EpbKeywords,
    RoleAssignments,
    UiModal,
  },
  props: {
    messages: {
      type: Object as PropType<UserRoleManagementMessages['invite']>,
      required: true,
    },
  },
  emits: {
    showSnackbar: (message: string) => {},
    showError: (error: Error) => {},
  },
  setup(props, { emit }) {
    const service = CONTAINER.get<UserInviteService>(TYPES.UserInviteService)

    const { setLoading } = useAppLoading()
    const { getErrorMessage } = useUsersError()

    const baseClass = 'epb-user-invite-modal'

    const sections = Object.entries(props.messages.sections).map(([id, { title }]) => ({
      id,
      title,
    }))

    const modal = ref()

    const emails = ref<Array<string>>([])
    const emailsError = ref<string | undefined>(undefined)

    const roleAssignments = ref<Array<RoleAssignmentDTO>>([])

    const valid = computed<boolean>(
      () =>
        !!emails.value.length &&
        !!roleAssignments.value.length &&
        roleAssignments.value.every(roleAssignment =>
          roleAssignment.conditions.every(
            roleAssignmentCondition => !!roleAssignmentCondition.value,
          ),
        ),
    )

    function open(): void {
      emails.value = []
      emailsError.value = undefined
      roleAssignments.value = []
      modal.value.open()
    }

    function setEmails(newEmails: Array<string>): void {
      const validEmails = newEmails.filter(isEmailValid)
      const hasError = validEmails.length < newEmails.length
      emails.value = validEmails
      emailsError.value = hasError ? props.messages.sections.details.email.error : undefined
    }

    async function accept(): Promise<void> {
      setLoading(true)
      const errorMessages: Record<string, ErrorMessage> = {}
      for (const email of emails.value) {
        try {
          await service.create({
            email,
            policies: roleAssignments.value.map(roleAssignment => ({
              roleId: roleAssignment.role.id,
              conditions: roleAssignment.conditions,
            })),
          })
        } catch (error) {
          errorMessages[email] = getErrorMessage(error)
        }
      }
      setLoading(false)
      if (!Object.keys(errorMessages).length) {
        modal.value.close()
        emit('showSnackbar', props.messages.success)
      } else {
        const error: Error =
          emails.value.length === 1
            ? Object.values(errorMessages)[0]
            : {
                title: props.messages.error,
                messages: Object.entries(errorMessages).map(
                  ([email, { title, message }]) => `<b>${title} (${email})</b><br>${message}`,
                ),
              }
        emit('showError', error)
      }
    }

    function isEmailValid(email: string): boolean {
      const validEmailRegex = /^[\w.+-]+@[a-z0-9.-]+\.[a-z]{2,6}$/i
      return validEmailRegex.test(email)
    }

    return {
      baseClass,
      sections,
      modal,
      emails,
      emailsError,
      roleAssignments,
      valid,
      open,
      setEmails,
      accept,
    }
  },
})
